/**
 * How the three roles are presented.
 *
 * Atlas / Comet / Manus are Caren's own taxonomy and are the names used
 * throughout the data and the CSS custom properties. Think / Clarify / Create
 * are what a visitor sees. Both sets are load-bearing: do not rename the
 * internal ones, and do not show them in the UI.
 *
 * These tables were previously copy-pasted across Home.tsx, ProblemFinder.tsx
 * and RoleContextCard.tsx. They live here now so the quiz and the grid cannot
 * drift apart.
 */

import type { Role } from "@/lib/toolsData";

export const ROLE_ICONS: Record<Role, string> = {
  Atlas: "🧠",
  Comet: "⚡",
  Manus: "✍️",
};

export const ROLE_LABELS: Record<Role, string> = {
  Atlas: "Think",
  Comet: "Clarify",
  Manus: "Create",
};

/**
 * One-line blurbs for the quiz's first step.
 *
 * ROLES[role].description in toolsData is two sentences and was being clamped
 * with an ellipsis in the hero, which reads as broken rather than brief. These
 * are condensed from that copy and are complete sentences at card width. The
 * full versions still appear in RoleContextCard above the grid, so nothing of
 * Caren's is lost: this is a second, shorter register for a smaller space.
 */
export const ROLE_SHORT: Record<Role, string> = {
  Atlas: "Research, plan, and organize your thinking.",
  Comet: "Fast answers, summaries, and comparisons.",
  Manus: "Turn ideas into content, images, and video.",
};

export interface RoleCardStyle {
  bg: string;
  border: string;
  dot: string;
  text: string;
  hover: string;
}

export const ROLE_CARD_STYLES: Record<Role, RoleCardStyle> = {
  Atlas: {
    bg: "bg-[oklch(0.32_0.08_255/0.06)]",
    border: "border-[oklch(0.32_0.08_255/0.25)]",
    dot: "bg-[oklch(0.32_0.08_255)]",
    text: "text-[oklch(0.32_0.08_255)]",
    hover: "hover:bg-[oklch(0.32_0.08_255/0.10)] hover:border-[oklch(0.32_0.08_255/0.45)]",
  },
  Comet: {
    bg: "bg-[oklch(0.55_0.12_70/0.06)]",
    border: "border-[oklch(0.55_0.12_70/0.25)]",
    dot: "bg-[oklch(0.55_0.12_70)]",
    text: "text-[oklch(0.55_0.12_70)]",
    hover: "hover:bg-[oklch(0.55_0.12_70/0.10)] hover:border-[oklch(0.55_0.12_70/0.45)]",
  },
  Manus: {
    bg: "bg-[oklch(0.38_0.09_155/0.06)]",
    border: "border-[oklch(0.38_0.09_155/0.25)]",
    dot: "bg-[oklch(0.38_0.09_155)]",
    text: "text-[oklch(0.38_0.09_155)]",
    hover: "hover:bg-[oklch(0.38_0.09_155/0.10)] hover:border-[oklch(0.38_0.09_155/0.45)]",
  },
};
